import { Injectable, NgZone } from '@angular/core';
import firebase from 'firebase/app';
import { Router } from '@angular/router';
import { AuthUser, UserDetail } from '../interface/auth.user';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AuthTabs, Constant } from '@app/@core';
import { RegisterForm } from '@app/formModels';
import { ProfileState } from '@app/@core/enums';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  user$: Observable<any>;
  userData: any;
  uid: any;
  isAdmin: any;
  fireUserId: any;

  constructor(
    private ngFireAuth: AngularFireAuth,
    private store: AngularFirestore,
    private router: Router,
    public ngZone: NgZone // NgZone service to remove outside scope warning
  ) {
    this.getUser();

    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.ngFireAuth.authState.subscribe((user) => {
      if (user) {
        this.userData = user;
        localStorage.setItem('user', JSON.stringify(this.userData));
        JSON.parse(localStorage.getItem('user'));
        // this.router.navigate(['/']);
      } else {
        localStorage.setItem('user', null);
        JSON.parse(localStorage.getItem('user'));
        // this.router.navigate(['/login']);
      }
    });
  }

  /* Setting up user data when sign in with username/password,
  sign up with username/password and sign in with social auth
  provider in Firestore database using AngularFirestore + AngularFirestoreDocument service */
  public SetUserData(user: any, payLoad?: any): Promise<void> {
    const userRef: AngularFirestoreDocument<any> = this.store.doc(`user/${user.uid}`);
    return userRef.update({ lastLogin: firebase.firestore.Timestamp.now(), ...payLoad });
  }

  // Returns true when user is looged in and email is verified
  get isLoggedIn(): boolean {
    const user = JSON.parse(localStorage.getItem('user'));
    return user !== null ? true : false;
  }

  // Sign in with email/password
  async SignIn(loginData: any): Promise<any> {
    let auth = await this.ngFireAuth.signInWithEmailAndPassword(loginData.email, loginData.password);
    let userDoc = await this.store.doc(`user/${auth.user.uid}`).get().toPromise();
    let user = userDoc.data() as UserDetail;
    if (user.role === 'superadmin') {
      this.SetUserData(auth.user);
    } else {
      if (!user.role) {
        let studyDoc = await this.store
          .collection('studies')
          .ref.where('contactEmail', 'array-contains', auth.user.email)
          .get();
        if (studyDoc.docs.length > 0) {
          this.SetUserData(auth.user, { role: 'admin' });
        } else {
          this.SetUserData(auth.user, { role: null });
        }
      } else {
        this.SetUserData(auth.user);
      }
    }
    return auth;
  }

  // Sign in with Google
  GoogleAuth() {
    const auth = firebase.auth();
    return this.AuthLogin(new firebase.auth.GoogleAuthProvider());
  }

  // Auth logic to run auth providers
  AuthLogin(provider: any) {
    return this.ngFireAuth
      .signInWithPopup(provider)
      .then((result: any) => {
        this.ngZone.run(() => {
          this.router.navigate(['/']);
        });
        this.SetUserData(result.user);
      })
      .catch((error: any) => {
        window.alert(error);
      });
  }

  // Sign out
  public signOut() {
    return this.ngFireAuth.signOut().then(() => {
      localStorage.removeItem('user');
      location.reload();
      this.router.navigate(['/login']);
    });
  }

  public getUser(): Observable<any> {
    return this.ngFireAuth.authState.pipe(
      switchMap((user: firebase.User) => {
        if (!user) {
          return of(null);
        }
        // let userD = this.store.collection('user').doc(user.uid).valueChanges();
        // userD.subscribe();
        return this.store.collection('user').doc(user.uid).valueChanges();
      }),
      take(1)
    );
  }

  public checkRole(user: UserDetail): boolean {
    if (!user) return false;
    return Constant.UserRoles.some((role) => role === user.role);
  }

  public async signup(payLoad: RegisterForm): Promise<any> {
    let authUser = await this.ngFireAuth.createUserWithEmailAndPassword(payLoad.email, payLoad.password);
    if (authUser.user) {
      this.store
        .collection('user')
        .doc(authUser.user.uid)
        .set({
          uuid: authUser.user.uid,
          firstName: payLoad.firstName,
          lastName: payLoad.lastName,
          email: authUser.user.email,
          createdAt: firebase.firestore.Timestamp.now(),
          photoURL: authUser.user.photoURL || null,
          profileState: ProfileState.STEP1,
        });
    }
    return authUser.user;
  }
}
